import { useEffect, useRef, useState } from "react";
import { Button, Input, Radio } from "@omnichannel/ui";
import { ApiError, request } from "./api";
interface Policy {
  enabled: boolean;
}
const message = (e: unknown) =>
  e instanceof ApiError
    ? e.message
    : "Не удалось загрузить или сохранить политику. Повторите запрос.";
export function AIRefundPolicy({
  appId,
  appName,
}: {
  appId: string;
  appName: string;
}) {
  const path = `/api/v1/apps/${appId}/ai-refund-policy`;
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const attempt = useRef<{ signature: string; key: string } | null>(null);
  const load = async () => {
    try {
      const policy = await request<Policy>(path);
      setEnabled(policy.enabled === true);
      setLoaded(true);
      setError("");
    } catch (e) {
      setError(message(e));
    }
  };
  useEffect(() => {
    void load();
  }, [path]);
  const save = async () => {
    if (!reason.trim()) {
      setError("Укажите причину изменения политики возврата");
      return;
    }
    setError("");
    setPending(true);
    try {
      const payload = { enabled, reason: reason.trim() };
      const signature = JSON.stringify(payload);
      if (attempt.current?.signature !== signature)
        attempt.current = { signature, key: crypto.randomUUID() };
      const policy = await request<Policy>(path, {
        method: "PUT",
        body: JSON.stringify({
          ...payload,
          idempotency_key: attempt.current.key,
        }),
      });
      setEnabled(policy.enabled);
      attempt.current = null;
      setReason("");
    } catch (e) {
      setError(message(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <section aria-label={`Возвраты AI ${appName}`}>
      <h3>Возврат при оплаченной AI-ошибке</h3>
      <p>
        Политика общая для приложения {appName}. При подтверждённой оплаченной
        ошибке клиенту возвращается списанная сумма, а стоимость провайдера
        оплачивает владелец. Требуется также включить возвраты в серверной
        интеграции ai-meter.
      </p>
      <p>
        Таймауты и неизвестные исходы требуют сверки; автоматический возврат для
        них не выполняется.
      </p>
      {!loaded && !error && <p role="status">Загрузка политики возврата…</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <fieldset disabled={!loaded || pending}>
          <legend>Возвраты для всех клиентов приложения {appName}</legend>
          <Radio
            name={`refund-${appId}`}
            label="Возвращать оплату подтверждённых AI-ошибок"
            checked={enabled}
            onChange={() => setEnabled(true)}
          />
          <Radio
            name={`refund-${appId}`}
            label="Не возвращать оплату подтверждённых AI-ошибок"
            checked={!enabled}
            onChange={() => setEnabled(false)}
          />
          <label>
            Причина политики возврата {appName}
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          </label>
          <Button type="submit">Сохранить политику возврата</Button>
        </fieldset>
      </form>
      {error && (
        <p role="alert">
          {error}{" "}
          <Button
            variant="ghost"
            onClick={() => void load()}
            disabled={pending}
          >
            Повторить загрузку политики возврата
          </Button>
        </p>
      )}
    </section>
  );
}
